/**
 * HepaticClinicalEnginePage — Clinical Engine UI за Hepatic IR модула.
 *
 * НЕ е свързано с LiverKidneyTwinPage (digital twin). Това е strictly клиничен
 * engine за инсулинова резистентност (etap 5/6 peer-reviewed + frozen).
 *
 * Flow:
 *   1) GET /api/clinical-engine/hepatic/prefill   → автоматично попълване
 *   2) Потребител попълва липсващите 11 биомаркера + поведенчески сигнали
 *   3) POST /api/clinical-engine/classify         → активация + flags + routes
 *   4) GET  /explain + /recommendations           → audit trail + интервенции
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, ArrowLeft, Activity, ShieldCheck, AlertTriangle,
  Pill, ChevronDown, ChevronRight, FlaskConical, Sparkles,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────
// Marker definitions — eng → BG label + unit hint
// ─────────────────────────────────────────────────────────────
const MARKERS = [
  { key: "ALT",             label: "ALT (Аланин-аминотрансфераза)",    unit: "U/L"    },
  { key: "AST",             label: "AST (Аспартат-аминотрансфераза)",  unit: "U/L"    },
  { key: "GGT",             label: "GGT (Гама-глутамил трансфераза)",  unit: "U/L"    },
  { key: "FASTING_INSULIN", label: "Гладен инсулин",                   unit: "mIU/L"  },
  { key: "FASTING_GLUCOSE", label: "Гладна глюкоза",                   unit: "mmol/L" },
  { key: "TG",              label: "Триглицериди",                     unit: "mmol/L" },
  { key: "HDL_C",           label: "HDL холестерол",                   unit: "mmol/L" },
  { key: "APOB",            label: "ApoB",                             unit: "g/L"    },
  { key: "BMI",             label: "BMI (Body Mass Index)",            unit: "kg/m²"  },
  { key: "WAIST",           label: "Талия",                            unit: "cm"     },
  { key: "PLATELETS",       label: "Тромбоцити",                       unit: "10⁹/L"  },
];

const DOMAIN_LABEL = {
  hepatic_A_biochemistry:        "A · Биохимия (ALT/AST/GGT)",
  hepatic_B_insulin_resistance:  "B · Инсулинова резистентност",
  hepatic_C_lipid_flux:          "C · Липиден поток",
  hepatic_D_steatosis:           "D · Стеатоза (FLI/HSI)",
  hepatic_E_fibrosis_indicators: "E · Фиброзни маркери",
};

const tierFor = (pct) => {
  if (pct >= 60) return { label: "Висока", color: "bg-rose-500", text: "text-rose-700", ring: "ring-rose-200" };
  if (pct >= 30) return { label: "Умерена", color: "bg-amber-500", text: "text-amber-700", ring: "ring-amber-200" };
  return            { label: "Ниска",   color: "bg-emerald-500", text: "text-emerald-700", ring: "ring-emerald-200" };
};

// ─────────────────────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────────────────────
const ActivationGauge = ({ pct }) => {
  const tier = tierFor(pct);
  const strokeColor = pct >= 60 ? "#f43f5e" : pct >= 30 ? "#f59e0b" : "#10b981";
  const r = 70, c = 2 * Math.PI * r;
  const offset = c - (c * Math.min(100, Math.max(0, pct))) / 100;
  return (
    <div className="flex flex-col items-center" data-testid="ce-activation-gauge">
      <div className="relative w-44 h-44">
        <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
          <circle cx="80" cy="80" r={r} stroke="#e5e7eb" strokeWidth="14" fill="none" />
          <circle
            cx="80" cy="80" r={r}
            stroke={strokeColor} strokeWidth="14" fill="none"
            strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-700"
          />
        </svg>
        <div className={`absolute inset-0 flex flex-col items-center justify-center ${tier.text}`}>
          <div className="text-4xl font-bold">{pct.toFixed(1)}%</div>
          <div className="text-xs uppercase tracking-wider">{tier.label} активация</div>
        </div>
      </div>
    </div>
  );
};

const DomainBar = ({ domain }) => {
  const pct = domain.activation_percent;
  const tier = tierFor(pct);
  return (
    <div className="space-y-1" data-testid={`ce-domain-${domain.domain_id}`}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-slate-700">{DOMAIN_LABEL[domain.domain_id] || domain.domain_id}</span>
        <span className={`font-mono text-xs ${tier.text}`}>
          {domain.capped_score.toFixed(1)} / {domain.cap} · {pct.toFixed(0)}%
        </span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-full ${tier.color} transition-all duration-700`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
};

const RuleRow = ({ rule, enriched }) => {
  const [open, setOpen] = useState(false);
  const statusColor = {
    positive:      "text-rose-600 border-rose-200 bg-rose-50",
    negative:      "text-emerald-600 border-emerald-200 bg-emerald-50",
    not_evaluable: "text-slate-500 border-slate-200 bg-slate-50",
    excluded:      "text-amber-600 border-amber-200 bg-amber-50",
  }[rule.status] || "text-slate-500 border-slate-200 bg-slate-50";

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 text-left"
        data-testid={`ce-rule-toggle-${rule.rule_id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <span className="font-mono text-xs text-slate-600">{rule.rule_id}</span>
          <Badge variant="outline" className={`text-xs ${statusColor}`}>{rule.status}</Badge>
          {rule.matched_level && (
            <Badge variant="secondary" className="text-xs">{rule.matched_level}</Badge>
          )}
          {enriched?.grade && (
            <Badge variant="outline" className="text-xs border-violet-200 text-violet-700 bg-violet-50">
              GRADE {enriched.grade}
            </Badge>
          )}
        </div>
        <div className="text-xs font-mono text-slate-600 shrink-0 ml-2">
          {rule.contribution > 0 ? `+${rule.contribution}` : rule.contribution} pts
        </div>
      </button>
      {open && (
        <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-200 space-y-2 text-sm">
          {enriched?.rationale_bg && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Клиничен смисъл</div>
              <p className="text-slate-700 whitespace-pre-line">{enriched.rationale_bg}</p>
            </div>
          )}
          {enriched?.references?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Референции</div>
              <ul className="text-xs text-slate-600 space-y-0.5">
                {enriched.references.slice(0, 5).map((ref, i) => (
                  <li key={i} className="font-mono">• {typeof ref === 'string' ? ref : (ref.pmid ? `PMID ${ref.pmid}` : JSON.stringify(ref))}</li>
                ))}
              </ul>
            </div>
          )}
          {rule.excluded_by_confounder && (
            <div className="text-xs text-amber-700">
              <Pill className="inline w-3 h-3 mr-1" />
              Изключено от confounder: <span className="font-mono">{rule.excluded_by_confounder}</span>
            </div>
          )}
          {rule.confidence_modifier !== 1 && (
            <div className="text-xs text-slate-600">
              Confidence modifier: <span className="font-mono">{rule.confidence_modifier}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function HepaticClinicalEnginePage() {
  const { API, user } = useAuth();
  const navigate = useNavigate();

  const [version, setVersion] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState({});
  const [meds, setMeds] = useState("");
  const [signals, setSignals] = useState({ alcohol_load: false });
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [loading, setLoading] = useState(false);
  const [classifyResult, setClassifyResult] = useState(null);
  const [explainResult, setExplainResult] = useState(null);
  const [recsResult, setRecsResult] = useState(null);
  const [classifying, setClassifying] = useState(false);

  // Initial load — version + prefill
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [v, p] = await Promise.all([
          axios.get(`${API}/clinical-engine/biology/version`),
          axios.get(`${API}/clinical-engine/hepatic/prefill`).catch(() => null),
        ]);
        if (!alive) return;
        setVersion(v.data);
        if (p?.data) {
          setPrefill(p.data);
          if (p.data.age) setAge(String(p.data.age));
          if (p.data.sex) setSex(p.data.sex);
          const next = {};
          for (const m of p.data.markers || []) next[m.marker] = String(m.value);
          setForm(next);
        }
      } catch (e) {
        toast.error("Грешка при зареждане на engine версията.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [API]);

  const coverage = useMemo(() => {
    const filled = MARKERS.filter(m => form[m.key] !== undefined && form[m.key] !== "").length;
    return { filled, total: MARKERS.length, pct: Math.round((filled / MARKERS.length) * 100) };
  }, [form]);

  const handleClassify = async () => {
    if (!age || !sex) { toast.error("Попълни възраст и пол."); return; }
    const measurements = {};
    const now = new Date().toISOString();
    for (const m of MARKERS) {
      const raw = form[m.key];
      if (raw === undefined || raw === "" || raw === null) continue;
      const val = parseFloat(raw);
      if (!Number.isFinite(val)) continue;
      measurements[m.key] = { value: val, unit: m.unit, measured_at: now };
    }
    if (Object.keys(measurements).length < 3) {
      toast.error("Попълни поне 3 биомаркера.");
      return;
    }
    const payload = {
      patient_id: user?._id || user?.id || `local-${Date.now()}`,
      context: {
        sex,
        age: parseInt(age, 10),
        population: "european",
        active_medications: meds.split(",").map(s => s.trim()).filter(Boolean),
        active_behavior_signals: Object.entries(signals).filter(([, v]) => v).map(([k]) => k),
      },
      measurements,
    };

    setClassifying(true);
    setClassifyResult(null);
    setExplainResult(null);
    setRecsResult(null);
    try {
      const { data: cls } = await axios.post(`${API}/clinical-engine/classify`, payload);
      setClassifyResult(cls);
      const pid = cls.patient_id;
      // Fetch explain + recs in parallel
      const [ex, rc] = await Promise.all([
        axios.get(`${API}/clinical-engine/hepatic/${pid}/explain`).catch(() => null),
        axios.get(`${API}/clinical-engine/hepatic/${pid}/recommendations`).catch(() => null),
      ]);
      if (ex?.data) setExplainResult(ex.data);
      if (rc?.data) setRecsResult(rc.data);
      toast.success("Класификацията е готова.");
      // Scroll to result
      setTimeout(() => {
        document.getElementById("ce-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error("Грешка при класификация: " + (e?.response?.data?.detail || e.message));
    } finally {
      setClassifying(false);
    }
  };

  const rulesById = useMemo(() => {
    const m = new Map();
    (explainResult?.rules || []).forEach(r => m.set(r.rule_id, r));
    return m;
  }, [explainResult]);

  const activation = classifyResult?.activation;
  const flagsFired = (classifyResult?.clinical_flags || []).filter(f => f.fired);
  const routesFired = (classifyResult?.safety_routes || []).filter(r => r.fired);
  const invariants = explainResult?.architectural_invariants_verified || {};
  const invariantsAllPass = Object.values(invariants).every(Boolean);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="ce-hepatic-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2" data-testid="ce-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Назад
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FlaskConical className="w-7 h-7 text-emerald-600" />
            Чернодробен клиничен модул
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Peer-reviewed engine за оценка на хепатална активация и инсулинова резистентност.
            14 правила · 5 домена · 4 фармакологични confounder-а · 1 safety route.
          </p>
        </div>
        {version && (
          <div className="hidden sm:flex flex-col items-end text-right" data-testid="ce-version-badge">
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 gap-1 mb-1">
              <Lock className="w-3 h-3" /> Engine FROZEN
            </Badge>
            <code className="text-[10px] text-slate-500 font-mono">
              {version.freeze_manifest_sha256.slice(0, 10)}…{version.freeze_manifest_sha256.slice(-6)}
            </code>
            <span className="text-[10px] text-slate-400 mt-0.5">{version.engine_version}</span>
          </div>
        )}
      </div>

      {/* Input form */}
      <Card data-testid="ce-input-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-600" />
            Биомаркери и контекст
          </CardTitle>
          <p className="text-xs text-slate-500">
            Попълнено: <span className="font-mono">{coverage.filled} / {coverage.total}</span> ({coverage.pct}%) ·
            {prefill ? (
              <span> Автоматично от лабораторни: <span className="font-mono">{prefill.markers?.length || 0}</span>.</span>
            ) : (
              <span> Няма налични лабораторни — попълни ръчно.</span>
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Demographics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Възраст</Label>
              <Input type="number" value={age} onChange={e => setAge(e.target.value)} placeholder="45" data-testid="ce-input-age" />
            </div>
            <div>
              <Label className="text-xs">Пол</Label>
              <select
                value={sex}
                onChange={e => setSex(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="ce-input-sex"
              >
                <option value="male">Мъж</option>
                <option value="female">Жена</option>
                <option value="other">Друг</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Активни медикаменти (отделени със запетая)</Label>
              <Input
                value={meds}
                onChange={e => setMeds(e.target.value)}
                placeholder="напр. metformin, fenofibrate"
                data-testid="ce-input-meds"
              />
            </div>
          </div>

          {/* Marker grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
            {MARKERS.map(m => {
              const prefillEntry = prefill?.markers?.find(x => x.marker === m.key);
              return (
                <div key={m.key}>
                  <Label className="text-xs flex items-center gap-1">
                    {m.label}
                    {prefillEntry && (
                      <Badge variant="outline" className="text-[9px] border-emerald-200 bg-emerald-50 text-emerald-700 px-1 py-0">
                        <Sparkles className="w-2.5 h-2.5 mr-0.5" /> auto
                      </Badge>
                    )}
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      value={form[m.key] ?? ""}
                      onChange={e => setForm(f => ({ ...f, [m.key]: e.target.value }))}
                      placeholder="—"
                      data-testid={`ce-input-${m.key}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-mono pointer-events-none">
                      {m.unit}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Behavior signals */}
          <div className="pt-2">
            <Label className="text-xs mb-1.5 block">Поведенчески сигнали</Label>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={signals.alcohol_load}
                  onChange={e => setSignals(s => ({ ...s, alcohol_load: e.target.checked }))}
                  data-testid="ce-signal-alcohol"
                />
                <span className="text-sm">Тежко алкохолно натоварване</span>
              </label>
            </div>
          </div>

          <Button
            onClick={handleClassify}
            disabled={classifying}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="ce-classify-btn"
          >
            {classifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
            {classifying ? "Анализирам…" : "Анализирай"}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {classifyResult && activation && (
        <div id="ce-result" className="space-y-4" data-testid="ce-results">
          {/* Activation summary */}
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">Активационен профил</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <ActivationGauge pct={activation.normalized_activation_percent} />
                <div className="md:col-span-2 space-y-3">
                  {activation.domains.map(d => <DomainBar key={d.domain_id} domain={d} />)}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-slate-100 text-xs">
                <div>
                  <div className="text-slate-500">Raw score</div>
                  <div className="font-mono">{activation.total_raw_score} / {activation.total_max_possible}</div>
                </div>
                <div>
                  <div className="text-slate-500">Confidence</div>
                  <div className="font-mono">{(classifyResult.overall_confidence * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div className="text-slate-500">Confounders</div>
                  <div className="font-mono">{classifyResult.confounder_annotations?.length || 0}</div>
                </div>
                <div>
                  <div className="text-slate-500">Hash</div>
                  <code className="text-[10px] text-slate-400">{classifyResult.engine_freeze_sha256?.slice(0, 10)}…</code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Narrative */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="ce-narrative-patient">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wider text-slate-500">За пациента</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-800 leading-relaxed">{classifyResult.patient_narrative_bg}</p>
              </CardContent>
            </Card>
            <Card data-testid="ce-narrative-clinician">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wider text-slate-500">За клинициста</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-800 text-sm leading-relaxed font-mono">{classifyResult.clinician_narrative_bg}</p>
              </CardContent>
            </Card>
          </div>

          {/* Flags + Safety routes + Confounders */}
          {(flagsFired.length > 0 || routesFired.length > 0 || classifyResult.confounder_annotations?.length > 0) && (
            <Card data-testid="ce-flags-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Клинични сигнали и безопасност
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {flagsFired.map(f => (
                  <div key={f.flag_id} className="border-l-4 border-rose-500 bg-rose-50 p-3 rounded" data-testid={`ce-flag-${f.flag_id}`}>
                    <div className="font-mono text-xs text-rose-700 mb-1">{f.flag_id}</div>
                    <p className="text-sm text-slate-700">{f.rationale_bg}</p>
                  </div>
                ))}
                {routesFired.map(r => (
                  <div key={r.route_id} className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded" data-testid={`ce-route-${r.route_id}`}>
                    <div className="font-mono text-xs text-amber-700 mb-1">SAFETY ROUTE · {r.route_id}</div>
                    <p className="text-sm text-slate-700">{r.rationale_bg}</p>
                  </div>
                ))}
                {classifyResult.confounder_annotations?.map((c, i) => (
                  <div key={i} className="border-l-4 border-violet-400 bg-violet-50 p-3 rounded" data-testid={`ce-confounder-${i}`}>
                    <div className="text-xs text-violet-700 mb-1">
                      <Pill className="inline w-3 h-3 mr-1" />
                      Confounder: <span className="font-mono">{c.confounder_id}</span> · {c.medication}
                    </div>
                    <p className="text-sm text-slate-700">
                      Действие: <span className="font-mono">{c.action}</span> върху правило <span className="font-mono">{c.affected_rule}</span>
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {recsResult?.recommendations?.length > 0 && (
            <Card data-testid="ce-recs-card">
              <CardHeader>
                <CardTitle className="text-lg">Препоръчителни интервенции</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recsResult.recommendations.map((r) => {
                  const priStyle = r.priority === "high"
                    ? { border: "border-rose-500", bg: "bg-rose-50", txt: "text-rose-700", bd: "border-rose-300" }
                    : r.priority === "medium"
                    ? { border: "border-amber-500", bg: "bg-amber-50", txt: "text-amber-700", bd: "border-amber-300" }
                    : { border: "border-emerald-500", bg: "bg-emerald-50", txt: "text-emerald-700", bd: "border-emerald-300" };
                  return (
                    <div
                      key={r.recommendation_id}
                      className={`border-l-4 ${priStyle.border} ${priStyle.bg} p-3 rounded`}
                      data-testid={`ce-rec-${r.recommendation_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-slate-800">{r.title_bg}</h4>
                        <Badge variant="outline" className={`text-xs ${priStyle.bd} ${priStyle.txt} bg-white`}>
                          {r.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-700">{r.rationale_bg}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Audit trail */}
          {explainResult && (
            <Card data-testid="ce-explain-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg">Audit trail (per-rule)</CardTitle>
                  {invariantsAllPass && (
                    <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 gap-1">
                      <ShieldCheck className="w-3 h-3" /> 3/3 архитектурни инварианти ✓
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {activation.domains.map(d => (
                  <div key={d.domain_id} className="space-y-1.5">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-3">
                      {DOMAIN_LABEL[d.domain_id]}
                    </div>
                    {d.rules.map(r => (
                      <RuleRow key={r.rule_id} rule={r} enriched={rulesById.get(r.rule_id)} />
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
